import { ActionIcon, Group, Paper, Stack, Text } from "@mantine/core";
import { Dropzone, FileRejection, MIME_TYPES } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconFileTypePdf, IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import "@mantine/dropzone/styles.css";

/** Same types and size the server and storage.rules accept for dealer uploads. */
const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png];
const MAX_SIZE = 20 * 1024 * 1024;

const formatSize = (bytes: number) =>
    bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const sameFile = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

interface CtcFilePickerProps {
    label: string;
    required?: boolean;
    files: File[];
    maxFiles: number;
    disabled?: boolean;
    onChange: (files: File[]) => void;
}

/** Drop zone plus a removable list: picking more files adds to the list, each file has its own remove button. */
export function CtcFilePicker({ label, required, files, maxFiles, disabled, onChange }: CtcFilePickerProps) {
    const remaining = maxFiles - files.length;

    const add = (picked: File[]) => {
        const fresh = picked.filter((f) => !files.some((existing) => sameFile(existing, f)));
        if (fresh.length > remaining) {
            notifications.show({ color: "orange", message: `At most ${maxFiles} files; ${fresh.length - remaining} not added` });
        }
        onChange([...files, ...fresh.slice(0, Math.max(0, remaining))]);
    };

    const reject = (rejections: FileRejection[]) => notifications.show({
        color: "red",
        title: "Some files were not added",
        message: rejections.map((r) => `${r.file.name}: ${r.errors[0]?.code === "file-too-large" ? "larger than 20 MB" : "only PDF, JPG or PNG"}`).join("\n"),
    });

    return (
        <Stack gap={6}>
            <Text size="sm" fw={500}>
                {label}
                {required && <Text span c="red"> *</Text>}
            </Text>

            <Dropzone onDrop={add} onReject={reject} accept={ACCEPT} maxSize={MAX_SIZE} multiple disabled={disabled || remaining <= 0} radius="md" p="md">
                <Group justify="center" gap="sm" mih={56} style={{ pointerEvents: "none" }}>
                    <Dropzone.Accept><IconUpload size={28} color="var(--mantine-color-blue-6)" /></Dropzone.Accept>
                    <Dropzone.Reject><IconX size={28} color="var(--mantine-color-red-6)" /></Dropzone.Reject>
                    <Dropzone.Idle><IconUpload size={28} stroke={1.5} color="var(--mantine-color-dimmed)" /></Dropzone.Idle>
                    <div>
                        <Text size="sm">{remaining > 0 ? "Drag files here or click to choose" : "Maximum number of files reached"}</Text>
                        <Text size="xs" c="dimmed">PDF, JPG or PNG · up to 20 MB each · {files.length}/{maxFiles} files</Text>
                    </div>
                </Group>
            </Dropzone>

            {files.map((file, index) => (
                <Paper key={`${file.name}-${file.size}-${file.lastModified}`} withBorder radius="md" p="xs">
                    <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                            {file.type === MIME_TYPES.pdf ? <IconFileTypePdf size={20} /> : <IconPhoto size={20} />}
                            <div style={{ minWidth: 0 }}>
                                <Text size="sm" truncate>{file.name}</Text>
                                <Text size="xs" c="dimmed">{formatSize(file.size)}</Text>
                            </div>
                        </Group>
                        <ActionIcon variant="subtle" color="red" aria-label={`Remove ${file.name}`} disabled={disabled}
                            onClick={() => onChange(files.filter((_, i) => i !== index))}>
                            <IconX size={16} />
                        </ActionIcon>
                    </Group>
                </Paper>
            ))}
        </Stack>
    );
}